/**
 * RoamerPredictiveIntelligenceEngine.ts — Engine #8: the Roamer Predictive
 * Intelligence Engine™.
 *
 * Analyzes trends, patterns, schedules, forecasts, and history to anticipate
 * future needs — proactive rather than reactive. It consumes signals from the
 * other engines through an injectable input gatherer (never hard-coupled),
 * runs the pure predictor rules over an input snapshot, and PUBLISHES typed
 * events (PredictionCreated, OpportunityDetected, RiskDetected,
 * PriceDropDetected, WeatherAlert, TravelAlert, MissionOpportunity) that
 * Core, Mission, Notifications, Local, and Workspace consume.
 *
 * Guarantees:
 *  • Deterministic: predictions are a pure function of (snapshot, now);
 *    deterministic ids so refreshes never duplicate and dismissals stick.
 *  • Every prediction carries confidence, data sources, supporting factors,
 *    a time window, and an expiration — explanations are mandatory.
 *  • Graceful degradation: missing input sources simply yield no predictions.
 *  • Distribution only — this engine never acts on its own predictions.
 */

import type { Prediction, PredictiveEventName, PredictiveInputSnapshot } from './models';
import { PREDICTORS, type Predictor } from './predictors';

const STORAGE_KEY = 'nomad:predictive:v1';

/**
 * Supplies the input snapshot. Injectable so tests are deterministic and so
 * adapters (Core, Fusion bridges) can wire live sources without this engine
 * importing them — the same dependency-inversion pattern as Roamer Core.
 */
export type InputGatherer = (
  now: number,
) => Promise<Partial<PredictiveInputSnapshot>> | Partial<PredictiveInputSnapshot>;

export interface PredictiveEvent {
  name: PredictiveEventName;
  prediction: Prediction;
  at: number;
}

type EventListener = (event: PredictiveEvent) => void;

interface PersistedState {
  dismissedIds: string[];
}

export class RoamerPredictiveIntelligenceEngine {
  private gatherer: InputGatherer = () => ({});
  private predictors: Predictor[] = PREDICTORS;
  private active: Prediction[] = [];
  private dismissedIds = new Set<string>();
  private listeners = new Set<EventListener>();
  private lastRefreshAt: number | null = null;

  constructor() {
    this.load();
  }

  // ── Wiring ──────────────────────────────────────────────────────────────────

  /** Wire the live input sources (called by an adapter, not by this engine). */
  setInputGatherer(gatherer: InputGatherer): void {
    this.gatherer = gatherer;
  }

  /** Test seam / future extension: replace the predictor set. */
  setPredictors(predictors: Predictor[]): void {
    this.predictors = predictors;
  }

  // ── Prediction cycle ────────────────────────────────────────────────────────

  /**
   * Gather inputs and recompute predictions. Publishes an event for every
   * NEWLY created prediction (same deterministic id ⇒ no duplicate events).
   */
  async refresh(now: number = Date.now()): Promise<Prediction[]> {
    let snapshot: Partial<PredictiveInputSnapshot> = {};
    try {
      snapshot = await this.gatherer(now);
    } catch {
      // a failing source must never break the prediction cycle
    }
    const previousIds = new Set(this.active.map((p) => p.id));
    const next: Prediction[] = [];
    const seen = new Set<string>();
    for (const predictor of this.predictors) {
      let produced: Prediction[] = [];
      try {
        produced = predictor(snapshot as PredictiveInputSnapshot, now);
      } catch {
        continue; // one broken rule never breaks the engine
      }
      for (const p of produced) {
        if (seen.has(p.id) || p.expiresAt <= now) continue;
        seen.add(p.id);
        next.push(p);
      }
    }
    this.active = next;
    this.lastRefreshAt = now;
    for (const p of next) {
      if (!previousIds.has(p.id) && !this.dismissedIds.has(p.id)) {
        this.publish({ name: p.eventType, prediction: structuredClone(p), at: now });
      }
    }
    return this.getPredictions(now);
  }

  // ── Output (consumed by other engines — never acted on here) ────────────────

  /** All live, non-dismissed predictions, ordered by confidence. */
  getPredictions(now: number = Date.now()): Prediction[] {
    return this.active
      .filter((p) => p.expiresAt > now && !this.dismissedIds.has(p.id))
      .sort((a, b) => b.confidence - a.confidence)
      .map((p) => structuredClone(p));
  }

  getOpportunities(now: number = Date.now()): Prediction[] {
    return this.getPredictions(now).filter((p) => p.kind === 'opportunity');
  }

  /** Proactive recommendations — generated BEFORE users search. */
  getRecommendations(now: number = Date.now()): Prediction[] {
    return this.getPredictions(now).filter((p) => p.kind === 'recommendation');
  }

  getLastRefreshAt(): number | null {
    return this.lastRefreshAt;
  }

  /** Dismissals persist across refreshes (deterministic ids make this work). */
  dismiss(predictionId: string): void {
    this.dismissedIds.add(predictionId);
    this.persist();
  }

  clearDismissals(): void {
    this.dismissedIds.clear();
    this.persist();
  }

  // ── Events ──────────────────────────────────────────────────────────────────

  onEvent(listener: EventListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private publish(event: PredictiveEvent): void {
    this.listeners.forEach((l) => {
      try {
        l(event);
      } catch {
        // one broken subscriber never breaks the engine
      }
    });
    // Window mirror for loosely-coupled consumers (same convention as Core).
    if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
      try {
        window.dispatchEvent(new CustomEvent(`nomad:predictive:${event.name}`, { detail: event }));
      } catch {
        /* non-browser environment */
      }
    }
  }

  // ── Persistence (dismissals only — predictions are always recomputed) ───────

  private persist(): void {
    if (typeof localStorage === 'undefined') return;
    try {
      const state: PersistedState = { dismissedIds: [...this.dismissedIds] };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      /* storage unavailable — keep working in memory */
    }
  }

  private load(): void {
    if (typeof localStorage === 'undefined') return;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const state = JSON.parse(raw) as Partial<PersistedState> | null;
      if (state == null || typeof state !== 'object') return;
      this.dismissedIds = new Set(
        (Array.isArray(state.dismissedIds) ? state.dismissedIds : []).filter(
          (id): id is string => typeof id === 'string',
        ),
      );
    } catch {
      /* corrupt state — start fresh rather than crash */
    }
  }
}

/** App-wide singleton (input gatherer must be wired by an adapter). */
export const roamerPredictiveIntelligenceEngine = new RoamerPredictiveIntelligenceEngine();
